import { Controller } from "@hotwired/stimulus";
import TomSelect from "tom-select";
import Cleave from 'cleave.js';

export default class extends Controller {
  static values = {
    maskOptions: Object
  }

  static MASK_CONFIGS = {
    date: {
      cleave: {
        date: true,
        datePattern: ['Y', 'm', 'd'],
        delimiter: '-',
        blocks: [4, 2, 2]
      },
      validator: function(value, config) {
        const pattern = config.datePattern;
        const delimiter = config.delimiter;
        const blocks = config.blocks;

        const regexPattern = pattern.map((part, index) => {
          const block = blocks[index];
          return `(\\d{${block}})`;
        }).join(`\\${delimiter}`);

        const dateRegex = new RegExp(`^${regexPattern}$`);
        if (!dateRegex.test(value)) return false;

        const [_, ...parts] = value.match(dateRegex);
        const dateValues = {};
        pattern.forEach((part, index) => {
          dateValues[part.toLowerCase()] = parseInt(parts[index]);
        });

        const date = new Date(
          dateValues.y,
          dateValues.m - 1,
          dateValues.d
        );

        return date instanceof Date && !isNaN(date) &&
               date.getDate() == dateValues.d &&
               date.getMonth() == dateValues.m - 1 &&
               date.getFullYear() == dateValues.y;
      }
    },
    currency: {
      cleave: {
        prefix: '$',
        numeral: true,
        numeralThousandsGroupStyle: 'thousand',
        noImmediatePrefix: true,
        numeralDecimalMark: '.',
        delimiter: ',',
        numeralDecimalScale: 2
      },
      validator: function(value) {
        const currencyRegex = /^\$\d{1,3}(,\d{3})*(\.\d{2})?$/;
        return currencyRegex.test(value);
      }
    }
  }

  connect() {
    if (this.element.tomselect) {
      this.element.tomselect.destroy();
    }

    const baseConfig = {
      plugins: ["remove_button", "clear_button"],
      create: this.data.get("create") === "true",
      maxItems: this.data.get("multiple-select") === "true" ? null : 1,
      placeholder: this.data.get("placeholder"),
      closeAfterSelect: !this.data.get("multiple-select"),
      sortField: { field: "text", direction: "asc" },
      persist: this.data.get("persist") === "true",
      hideSelected: this.data.get("hide-selected") === "true",
      allowEmptyOption: true,
      render: this.getRenderConfigWithMask(this.data.get("render-type") || 'default'),
      score: (search) => {
        const maskType = this.data.get("mask-type");
        search = search.toLowerCase();
        
        let cleanSearch = search;
        if (maskType === 'currency') {
          cleanSearch = search.replace(/[$,]/g, '');
        }
        if (maskType === 'date') {
          cleanSearch = search.replace(/[/-]/g, '');
        }
        return function(item) {
          if (!search.length) return 1;
          
          const text = item.text.toLowerCase();
          const originalText = (item.originalText || '').toLowerCase();
          
          let cleanText = text;
          if (maskType === 'currency') {
            cleanText = text.replace(/[$,]/g, '');
          }
          if (maskType === 'date') {
            cleanText = text.replace(/[/-]/g, '');
          }
          
          if (text.includes(search)) {
            return 1;
          }
          
          if (originalText && originalText.includes(search)) {
            return 1;
          }
          
          if (cleanText.includes(cleanSearch)) {
            return 1;
          }
          
          return 0;
        };
      },
      onType: (str) => this.applyMask(str),
      createFilter: (input) => this.validateInput(input),
      create: (input) => {
        return {
          value: this.getUnmaskedValue(input),
          text: this.applyMask(input),
          originalText: input
        };
      },
      onItemAdd: () => {
        this.element.tomselect.control_input.value = '';
        if (this.cleave) {
          this.cleave.setRawValue('');
        }
      }
    };

    const ajaxUse = this.data.get("ajax-use") === "true";
    const ajaxUrlBase = this.data.get("ajax-url-base");
    const ajaxMinLength = parseInt(this.data.get("ajax-min-length") || "2");
    const ajaxSearchFields = (this.data.get("ajax-search-fields") || "text").split(",");

    if (ajaxUse && ajaxUrlBase) {
      Object.assign(baseConfig, {
        valueField: 'id',
        labelField: 'text',
        searchField: ajaxSearchFields,
        loadThrottle: 300,
        load: function(query, callback) {
          if (!query.length) return callback();

          fetch(`${ajaxUrlBase}?q=${encodeURIComponent(query)}`, {
            headers: {
              'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
            }
          })
          .then(response => response.json())
          .then(data => {
            callback(data);
          })
          .catch(() => {
            callback();
          });
        },
        shouldLoad: function(query) {
          return query.length >= ajaxMinLength;
        }
      });
    }

    this.element.tomselect = new TomSelect(this.element, baseConfig);
    this.initializeMask();
  }

  initializeMask() {
    const maskType = this.data.get("mask-type");
    if (!maskType || !this.constructor.MASK_CONFIGS[maskType]) return;

    const input = this.element.tomselect.control_input;
    const config = this.getMaskConfig();

    this.cleave = new Cleave(input, config.cleave);
  }

  getMaskConfig() {
    const maskType = this.data.get("mask-type");
    const defaultConfig = this.constructor.MASK_CONFIGS[maskType];
    const customConfig = this.maskOptionsValue || {};

    return {
      cleave: { ...defaultConfig.cleave, ...customConfig },
      validator: customConfig.validator || defaultConfig.validator
    };
  }

  applyMask(value) {
    if (!value) return value;
    const maskType = this.data.get("mask-type");
    if (!maskType || !this.constructor.MASK_CONFIGS[maskType]) return value;

    const tempInput = document.createElement('input');
    const config = this.getMaskConfig();
    const cleave = new Cleave(tempInput, config.cleave);

    cleave.setRawValue(value);
    return cleave.getFormattedValue();
  }

  validateInput(value) {
    const maskType = this.data.get("mask-type");
    if (!maskType || !this.constructor.MASK_CONFIGS[maskType]) return true;

    const config = this.getMaskConfig();
    return config.validator(value, config.cleave);
  }

  disconnect() {
    if (this.cleave) {
      this.cleave.destroy();
    }
    if (this.element.tomselect) {
      this.element.tomselect.destroy();
    }
  }

  getRenderConfig(type) {
    const renderConfigs = {
      default: {
        option: this.renderDefaultView,
        item: this.renderDefaultView
      },

      person_view: {
        option: this.renderPersonOption,
        item: this.renderDefaultView
      },

      person_view_v2: {
        option: this.renderPersonOptionV2,
        item: this.renderDefaultView
      }
    };

    return renderConfigs[type] || renderConfigs.default;
  }

  renderDefaultView = (item, escape) => {
    return `
      <div>${escape(item.text)}</div>
    `;
  }

  renderPersonOption = (item, escape) => {
    return `
      <div class="flex flex-col py-1">
        <span class="font-medium">${escape(item.text)}</span>
        <span class="text-sm text-gray-500">${escape(item.email)}</span>
        <span class="text-sm text-gray-500">${escape(item.phone)}</span>
        <span class="text-sm text-gray-500">${escape(item.role)}</span>
      </div>
    `;
  }

  renderPersonOptionV2 = (item, escape) => {
    return `
      <div class="flex flex-col py-1.5 px-2 hover:bg-gray-50">
        <div class="flex items-center gap-2">
          <i class="fas fa-user-circle text-primary-500"></i>
          <span class="font-semibold text-gray-900">${escape(item.text)}</span>
        </div>
        <div class="flex flex-col gap-0.5 pl-6 mt-0.5">
          <div class="flex items-center gap-1.5 text-xs text-gray-600">
            <i class="fas fa-envelope text-gray-400 w-3"></i>
            <span>${escape(item.email)}</span>
          </div>
          ${item.phone ? `
            <div class="flex items-center gap-1.5 text-xs text-gray-600">
              <i class="fas fa-phone text-gray-400 w-3"></i>
              <span>${escape(item.phone)}</span>
            </div>
          ` : ''}
          ${item.role ? `
            <div class="flex items-center gap-1.5 text-xs text-gray-600">
              <i class="fas fa-briefcase text-gray-400 w-3"></i>
              <span>${escape(item.role)}</span>
            </div>
          ` : ''}
        </div>
      </div>
    `;
  }

  getRenderConfigWithMask(renderType) {
    const baseRender = this.getRenderConfig(renderType);
    const type = this.data.get("mask-type");

    if (!type) return baseRender;

    return {
      ...baseRender,
      option: (data, escape) => {
        data.masked_text = this.applyMask(data.text);
        const originalData = { ...data };
        data.text = data.masked_text;
        const result = baseRender.option(data, escape);
        data.text = originalData.text;
        return result;
      },
      item: (data, escape) => {
        data.masked_text = this.applyMask(data.text);
        const originalData = { ...data };
        data.text = data.masked_text;
        const result = baseRender.item(data, escape);
        data.text = originalData.text;
        return result;
      }
    };
  }

  getUnmaskedValue(value) {
    if (!value) return value;
    const maskType = this.data.get("mask-type");
    if (!maskType || !this.constructor.MASK_CONFIGS[maskType]) return value;

    // For currency, remove $ and commas
    if (maskType === 'currency') {
      return value.replace(/[$,]/g, '');
    }
    
    // For dates, keep the standardized format
    if (maskType === 'date') {
      // If it's already in YYYY-MM-DD format, return as is
      // Otherwise, try to extract the date components from the masked value
      const config = this.getMaskConfig();
      const datePattern = config.cleave.datePattern;
      const delimiter = config.cleave.delimiter;
      
      const parts = value.split(delimiter);
      if (parts.length !== datePattern.length) return value;
      
      const dateObj = {};
      datePattern.forEach((part, index) => {
        dateObj[part.toLowerCase()] = parts[index];
      });
      
      // Return in standard format YYYY-MM-DD
      return `${dateObj.y}-${dateObj.m}-${dateObj.d}`;
    }
    
    return value;
  }
}